import React from 'react';
import { useRouteError } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Home, ChevronDown, Bug, Send, CheckCircle, Mail } from 'lucide-react';
import { supabaseService } from '../services/supabaseService';
import { getCapturedLogs, getLogsAsString, getSystemInfo } from '../utils/consoleCapture';
import { openEmailInGmail } from '../utils/gmailCompose';

// Developer email for reports
const DEVELOPER_EMAIL = 'jrsschroeder@gmail.com';

/**
 * Error Boundary Component
 * 
 * Catches JavaScript errors in child components and displays a friendly
 * error page that matches the site's Apple-style design.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null, 
      showDetails: false,
      sendingReport: false,
      reportSent: false,
      reportError: null
    };
  }

  static getDerivedStateFromError(error) {
    // Ignore Firestore internal SDK errors - they don't affect the app
    if (error?.message?.includes('FIRESTORE') && error?.message?.includes('INTERNAL ASSERTION')) {
      console.warn('⚠️ Firestore SDK error ignored by ErrorBoundary');
      return { hasError: false, error: null };
    }
    
    // Handle chunk load errors — purge SW cache then hard-reload
    if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk')) {
      console.warn('⚠️ ChunkLoadError detected — purging SW cache and reloading…');
      const lastReload = sessionStorage.getItem('chunk_reload');
      const now = Date.now();
      if (!lastReload || (now - parseInt(lastReload)) > 30000) {
        sessionStorage.setItem('chunk_reload', now.toString());
        // Tell the SW to purge all caches, then hard-reload
        if (navigator.serviceWorker?.controller) {
          navigator.serviceWorker.controller.postMessage('purgeAndReload');
          // Also listen for the SW's confirmation, then reload
          navigator.serviceWorker.addEventListener('message', (evt) => {
            if (evt.data === 'cachesPurged') window.location.reload();
          });
          // Fallback: reload after 2s even if SW doesn't respond
          setTimeout(() => window.location.reload(), 2000);
        } else {
          // No SW controlling — just clear caches directly and reload
          if ('caches' in window) {
            caches.keys().then((names) =>
              Promise.all(names.map((n) => caches.delete(n)))
            ).then(() => window.location.reload());
          } else {
            window.location.reload();
          }
        }
        return { hasError: false, error: null };
      }
      // If we already tried recently, show the error with a manual reload button
      return { hasError: true, error, isChunkError: true };
    }
    
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Ignore Firestore internal SDK errors
    if (error?.message?.includes('FIRESTORE') && error?.message?.includes('INTERNAL ASSERTION')) {
      console.warn('⚠️ Firestore SDK internal error (suppressed):', error.message);
      return;
    }
    console.error('❌ ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, reportSent: false });
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, reportSent: false });
    window.location.href = '/';
  };

  handleSendReport = async () => {
    const { error, errorInfo } = this.state;
    
    this.setState({ sendingReport: true, reportError: null });

    try {
      // Get current user info from localStorage if available
      let userEmail = 'unknown';
      let userName = 'Unknown User';
      try {
        const authData = localStorage.getItem('firebase:authUser:AIzaSyAqhyoHC88TN4lk2BmgpEEWvwfRdwqK1tQ:[DEFAULT]');
        if (authData) {
          const parsed = JSON.parse(authData);
          userEmail = parsed.email || 'unknown';
          userName = parsed.displayName || parsed.email || 'Unknown User';
        }
      } catch (e) {
        console.warn('Could not get user info:', e);
      }

      // Gather system info
      const systemInfo = getSystemInfo();
      
      // Get captured console logs
      const consoleLogs = getCapturedLogs();
      const logsString = getLogsAsString();

      // Build the report
      const report = {
        // Error details
        errorMessage: error?.message || 'Unknown error',
        errorStack: error?.stack || '',
        componentStack: errorInfo?.componentStack || '',
        
        // User info
        userEmail,
        userName,
        
        // System info
        url: systemInfo.url,
        userAgent: systemInfo.userAgent,
        platform: systemInfo.platform,
        screenSize: systemInfo.screenSize,
        viewportSize: systemInfo.viewportSize,
        timezone: systemInfo.timezone,
        
        // Console logs (last 50)
        consoleLogs: consoleLogs.slice(-50),
        consoleLogsText: logsString.slice(-10000), // Last 10KB
        
        // Timestamps
        errorTimestamp: systemInfo.timestamp
      };

      // Submit to Firestore (for backup/viewing in console)
      const firestoreResult = await supabaseService.submitErrorReport(report);
      console.log('Firestore result:', firestoreResult);

      // Send email via FormSubmit (free email service)
      const emailResponse = await fetch('https://formsubmit.co/ajax/jrsschroeder@gmail.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          _subject: `Error Report: ${report.errorMessage.substring(0, 50)}`,
          error_message: report.errorMessage,
          error_stack: report.errorStack?.substring(0, 2000) || 'N/A',
          user_email: report.userEmail,
          user_name: report.userName,
          url: report.url,
          timestamp: report.errorTimestamp,
          browser: report.userAgent,
          screen_size: report.screenSize,
          console_logs: report.consoleLogsText?.substring(0, 5000) || 'N/A',
          _template: 'table'
        })
      });

      if (emailResponse.ok) {
        this.setState({ reportSent: true, sendingReport: false });
      } else {
        // Even if email fails, Firestore backup worked
        if (firestoreResult.success) {
          this.setState({ reportSent: true, sendingReport: false });
        } else {
          throw new Error('Failed to send report');
        }
      }
    } catch (err) {
      console.error('Failed to send error report:', err);
      this.setState({ 
        sendingReport: false, 
        reportError: err.message 
      });
    }
  };

  handleEmailFallback = () => {
    const { error, errorInfo } = this.state;
    const systemInfo = getSystemInfo();
    const subject = `Error Report: ${error?.message?.substring(0, 50) || 'Unknown error'}`;
    const body =
      `Error Report\n` +
      `============\n\n` +
      `Error: ${error?.message || 'Unknown'}\n\n` +
      `URL: ${systemInfo.url}\n` +
      `Time: ${systemInfo.timestamp}\n` +
      `Browser: ${systemInfo.userAgent}\n\n` +
      `Stack Trace:\n${error?.stack || 'N/A'}\n\n` +
      `Please describe what you were doing when this error occurred:\n\n`;
    openEmailInGmail(DEVELOPER_EMAIL, { subject, body });
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, showDetails, sendingReport, reportSent, reportError } = this.state;
      
      return (
        <div className="min-h-screen bg-surface-2 dark:bg-ink flex items-center justify-center p-6">
          <div className="max-w-lg w-full">
            {/* Main Card */}
            <div className="bg-surface rounded-xl shadow-lg overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-br from-danger to-warning p-8 text-center">
                <div className="w-20 h-20 bg-white/20 backdrop-blur-xl rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-10 h-10 text-white" strokeWidth={1.5} />
                </div>
                <h1 className="text-2xl font-semibold text-white mb-2">
                  Oops! Something went wrong
                </h1>
                <p className="text-white/80 text-sm">
                  We encountered an unexpected error
                </p>
              </div>
              
              {/* Content */}
              <div className="p-8">
                {/* Error Message */}
                <div className="bg-surface-2 dark:bg-ink rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Bug className="w-5 h-5 text-danger shrink-0 mt-0.5" />
                    <p className="text-[15px] text-ink break-words">
                      {error?.message || 'An unexpected error occurred'}
                    </p>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="space-y-3">
                  <button
                    onClick={this.handleReload}
                    className="w-full h-10 bg-brand hover:bg-brand-hover text-white rounded-xl font-medium text-[15px] transition-all flex items-center justify-center gap-2 shadow-lg shadow-brand/25"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Reload Page
                  </button>
                  
                  <button
                    onClick={this.handleGoHome}
                    className="w-full h-10 bg-surface-2 dark:bg-[#3a3a3c] hover:bg-[#e8e8ed] dark:hover:bg-[#48484a] text-ink rounded-xl font-medium text-[15px] transition-all flex items-center justify-center gap-2"
                  >
                    <Home className="w-4 h-4" />
                    Go to Dashboard
                  </button>
                </div>
                
                {/* Send Report Section */}
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-white/10">
                  <p className="text-[13px] text-ink-muted mb-3 text-center">
                    Help us fix this issue by sending an error report
                  </p>
                  
                  {reportSent ? (
                    <div className="bg-positive/10 rounded-xl p-4 flex items-center justify-center gap-2 text-positive">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Report sent! Thank you for helping us improve.</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <button
                        onClick={this.handleSendReport}
                        disabled={sendingReport}
                        className="w-full h-10 bg-positive hover:bg-positive disabled:bg-ink-muted text-white rounded-xl font-medium text-[15px] transition-all flex items-center justify-center gap-2"
                      >
                        {sendingReport ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            Send Report to Developer
                          </>
                        )}
                      </button>
                      
                      {reportError && (
                        <div className="bg-danger/10 rounded-xl p-3 text-center">
                          <p className="text-[13px] text-danger mb-2">
                            Failed to send report: {reportError}
                          </p>
                          <button
                            onClick={this.handleEmailFallback}
                            className="text-[13px] text-brand hover:underline flex items-center justify-center gap-1"
                          >
                            <Mail className="w-3 h-3" />
                            Send via email instead
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Technical Details Toggle */}
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-white/10">
                  <button
                    onClick={() => this.setState({ showDetails: !showDetails })}
                    className="w-full flex items-center justify-between text-[13px] text-ink-muted hover:text-ink dark:hover:text-white transition-colors"
                  >
                    <span>Technical Details</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showDetails && (
                    <div className="mt-4 space-y-3">
                      <div className="bg-ink dark:bg-black rounded-xl p-4 overflow-x-auto max-h-48 overflow-y-auto">
                        <pre className="text-[11px] text-ink-muted font-mono whitespace-pre-wrap break-words">
                          <span className="text-danger">Error:</span> {error?.message}
                          {'\n\n'}
                          <span className="text-warning">Stack:</span>
                          {'\n'}{error?.stack}
                        </pre>
                      </div>
                      
                      {errorInfo?.componentStack && (
                        <div className="bg-ink dark:bg-black rounded-xl p-4 overflow-x-auto max-h-48 overflow-y-auto">
                          <pre className="text-[11px] text-ink-muted font-mono whitespace-pre-wrap break-words">
                            <span className="text-brand">Component Stack:</span>
                            {errorInfo.componentStack}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <p className="text-center text-[13px] text-ink-muted mt-6">
              If this problem persists, please contact support.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Route Error Page Component
 * 
 * Used as the errorElement for React Router routes.
 * Provides a consistent error experience for route-level errors.
 */
export function RouteErrorPage() {
  const routeError = useRouteError();
  const [sendingReport, setSendingReport] = React.useState(false);
  const [reportSent, setReportSent] = React.useState(false);
  const [reportError, setReportError] = React.useState(null);

  const errorMessage = (routeError?.message ?? (typeof routeError === 'string' ? routeError : '')) || 'Route Error - Page failed to load';
  const errorStack = (routeError?.stack ?? (routeError?.componentStack ? `Component stack:\n${routeError.componentStack}` : '')) || 'React Router errorElement triggered';

  React.useEffect(() => {
    if (routeError != null) {
      console.error('Route error:', errorMessage, errorStack);
    }
  }, [routeError, errorMessage, errorStack]);

  // Auto-reload on chunk errors (happens after deployments)
  React.useEffect(() => {
    const isChunkError =
      routeError?.name === 'ChunkLoadError' ||
      errorMessage?.includes('Loading chunk') ||
      errorMessage?.includes('Failed to fetch dynamically imported');

    if (!isChunkError) return;

    const lastReload = sessionStorage.getItem('chunk_reload');
    const now = Date.now();
    if (!lastReload || (now - parseInt(lastReload)) > 30000) {
      console.warn('⚠️ Route-level ChunkLoadError — purging SW cache and reloading…');
      sessionStorage.setItem('chunk_reload', now.toString());
      // Purge caches then reload
      if ('caches' in window) {
        caches.keys().then((names) =>
          Promise.all(names.map((n) => caches.delete(n)))
        ).then(() => window.location.reload());
      } else {
        window.location.reload();
      }
    }
  }, [routeError, errorMessage]);

  const handleReload = () => {
    // Clear the chunk reload tracker and purge caches for a clean reload
    sessionStorage.removeItem('chunk_reload');
    if ('caches' in window) {
      caches.keys().then((names) =>
        Promise.all(names.map((n) => caches.delete(n)))
      ).then(() => window.location.reload());
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleSendReport = async () => {
    setSendingReport(true);
    setReportError(null);

    try {
      // Get current user info from localStorage if available
      let userEmail = 'unknown';
      let userName = 'Unknown User';
      try {
        const authData = localStorage.getItem('firebase:authUser:AIzaSyAqhyoHC88TN4lk2BmgpEEWvwfRdwqK1tQ:[DEFAULT]');
        if (authData) {
          const parsed = JSON.parse(authData);
          userEmail = parsed.email || 'unknown';
          userName = parsed.displayName || parsed.email || 'Unknown User';
        }
      } catch (e) {
        console.warn('Could not get user info:', e);
      }

      // Gather system info
      const systemInfo = getSystemInfo();
      
      // Get captured console logs
      const consoleLogs = getCapturedLogs();
      const logsString = getLogsAsString();

      // Build the report (include actual route error when available)
      const report = {
        errorMessage,
        errorStack: errorStack?.substring?.(0, 5000) ?? errorStack ?? 'React Router errorElement triggered',
        componentStack: routeError?.componentStack ?? '',
        userEmail,
        userName,
        url: systemInfo.url,
        userAgent: systemInfo.userAgent,
        platform: systemInfo.platform,
        screenSize: systemInfo.screenSize,
        viewportSize: systemInfo.viewportSize,
        timezone: systemInfo.timezone,
        consoleLogs: consoleLogs.slice(-50),
        consoleLogsText: logsString.slice(-10000),
        errorTimestamp: systemInfo.timestamp
      };

      // Submit to Firestore (for backup/viewing in console)
      const firestoreResult = await supabaseService.submitErrorReport(report);
      console.log('Firestore result:', firestoreResult);

      // Send email via FormSubmit (free email service)
      const emailResponse = await fetch('https://formsubmit.co/ajax/jrsschroeder@gmail.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          _subject: `Error Report: ${report.errorMessage.substring(0, 50)}`,
          error_message: report.errorMessage,
          error_stack: report.errorStack?.substring(0, 2000) || 'N/A',
          user_email: report.userEmail,
          user_name: report.userName,
          url: report.url,
          timestamp: report.errorTimestamp,
          browser: report.userAgent,
          screen_size: report.screenSize,
          console_logs: report.consoleLogsText?.substring(0, 5000) || 'N/A',
          _template: 'table'
        })
      });

      if (emailResponse.ok) {
        setReportSent(true);
      } else {
        // Even if email fails, Firestore backup worked
        if (firestoreResult.success) {
          setReportSent(true);
        } else {
          throw new Error('Failed to send report');
        }
      }
    } catch (err) {
      console.error('Failed to send error report:', err);
      setReportError(err.message);
    } finally {
      setSendingReport(false);
    }
  };

  const handleEmailFallback = () => {
    const systemInfo = getSystemInfo();
    const subject = 'Error Report: Page failed to load';
    const body =
      `Error Report\n` +
      `============\n\n` +
      `Error: Route/Page failed to load\n\n` +
      `URL: ${systemInfo.url}\n` +
      `Time: ${systemInfo.timestamp}\n` +
      `Browser: ${systemInfo.userAgent}\n\n` +
      `Please describe what you were doing when this error occurred:\n\n`;
    openEmailInGmail(DEVELOPER_EMAIL, { subject, body });
  };

  return (
    <div className="min-h-screen bg-surface-2 flex items-center justify-center p-6">
      <div className="max-w-lg w-full">
        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-danger to-warning p-8 text-center">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-xl rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-10 h-10 text-white" strokeWidth={1.5} />
            </div>
            <h1 className="text-2xl font-semibold text-white mb-2">
              Oops! Something went wrong
            </h1>
            <p className="text-white/80 text-sm">
              We couldn't load this page
            </p>
          </div>
          
          {/* Content */}
          <div className="p-8">
            <p className="text-[15px] text-ink-muted text-center mb-6">
              Don't worry, this happens sometimes. Try reloading the page or going back to the dashboard.
            </p>
            
            {/* Actions */}
            <div className="space-y-3">
              <button
                onClick={handleReload}
                className="w-full h-10 bg-brand hover:bg-brand-hover text-white rounded-xl font-medium text-[15px] transition-all flex items-center justify-center gap-2 shadow-lg shadow-brand/25"
              >
                <RefreshCw className="w-4 h-4" />
                Reload Page
              </button>
              
              <button
                onClick={handleGoHome}
                className="w-full h-10 bg-surface-2 hover:bg-[#e8e8ed] text-ink rounded-xl font-medium text-[15px] transition-all flex items-center justify-center gap-2"
              >
                <Home className="w-4 h-4" />
                Go to Dashboard
              </button>
            </div>
            
            {/* Send Report Section */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-[13px] text-ink-muted mb-3 text-center">
                Help us fix this issue by sending an error report
              </p>
              
              {reportSent ? (
                <div className="bg-positive/10 rounded-xl p-4 flex items-center justify-center gap-2 text-positive">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Report sent! Thank you for helping us improve.</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <button
                    onClick={handleSendReport}
                    disabled={sendingReport}
                    className="w-full h-10 bg-positive hover:bg-positive disabled:bg-ink-muted text-white rounded-xl font-medium text-[15px] transition-all flex items-center justify-center gap-2"
                  >
                    {sendingReport ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Report to Developer
                      </>
                    )}
                  </button>
                  
                  {reportError && (
                    <div className="bg-danger/10 rounded-xl p-3 text-center">
                      <p className="text-[13px] text-danger mb-2">
                        Failed to send report: {reportError}
                      </p>
                      <button
                        onClick={handleEmailFallback}
                        className="text-[13px] text-brand hover:underline flex items-center justify-center gap-1 mx-auto"
                      >
                        <Mail className="w-3 h-3" />
                        Send via email instead
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <p className="text-center text-[13px] text-ink-muted mt-6">
          If this problem persists, please contact support.
        </p>
      </div>
    </div>
  );
}

export default ErrorBoundary;
